import CloseIcon from "@mui/icons-material/Close";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Tooltip from "@mui/material/Tooltip";
import { useContext, useEffect, useRef, useState } from "react";
import { InfoCircle } from "react-bootstrap-icons";
import "../../../../Tooltip/styles/tooltip.css";
import { DashboardContext } from "../../../DashboardContext/DashboardContextProvider";
import { FormContext } from "../context/FormProvider.jsx";
import * as Messages from "../messages/messages.js";
import "../styles/form-container.css";
import "./styles/text-field.css";

// AuthorizedUserInput component is used to render a text input field in the form (Names, Addresses, etc.)
const AuthorizedUserInput = ({
  idProp,
  label,
  val,
  state,
  setState,
  req,
  debouncedSave,
  setIsLoading,
  disabledInput,
  readOnly,
  type,
}) => {
  const textRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [openHelper, setOpenHelper] = useState(false);
  const [showPlaceholder, setShowPlaceholder] = useState(false);
  const [placeholder, setPlaceholder] = useState("");
  const [adjustMargin, setAdjustMargin] = useState(false);
  const { requestStatus, handleInputFocus, handleInputDisable } =
    useContext(FormContext);
  const { capitalizeNameAfterSpace, userId } = useContext(DashboardContext);

  useEffect(() => {
    if (req && openHelper) {
      setAdjustMargin(true);
    } else {
      setAdjustMargin(false);
    }
  }, [req, openHelper]);

  // Function to handle text input change
  const handleTextChange = (e) => {
    let newState;
    if (idProp === "signer") {
      newState = {
        ...state,
        authorizationLetter: {
          ...state["authorizationLetter"],
          [idProp]: capitalizeNameAfterSpace(e.target.value),
        },
      };
    } else {
      newState = {
        ...state,
        authorizationLetter: {
          ...state["authorizationLetter"],
          [idProp]: e.target.value,
        },
      };
    }
    setState(newState);
  };

  return (
    <div className={`text-info-container ${type}-info-container`}>
      <FormControl
        variant="outlined"
        id="text-form"
        classes={{ root: "form-text-root" }}
        className={`${type}-form-control ${
          handleInputDisable(idProp, state, disabledInput, userId) === true
            ? "disable-text"
            : req
              ? "required"
              : ""
        }`}
      >
        <InputLabel
          id="text-field-label"
          htmlFor="text-input"
          classes={{ shrink: "text-field-label-shrink" }}
          className={`${idProp}-text-field-label`}
        >
          {`${label}`}
        </InputLabel>
        <OutlinedInput
          type="text"
          id="text-field"
          className={`${idProp}-input`}
          ref={textRef}
          value={val}
          onChange={handleTextChange}
          onFocus={() => handleInputFocus(textRef, setOpenHelper)}
          onSelect={() => {
            if (state[`${idProp}`] === "") {
              setOpen(true);
            } else {
              setOpen(false);
            }
            setShowPlaceholder(true);
          }}
          onBlur={() => {
            setOpen(false);
            setShowPlaceholder(false);
            setPlaceholder("");
          }}
          disabled={
            readOnly ||
            handleInputDisable(idProp, state, disabledInput, userId) === true
              ? true
              : false
          }
          autoComplete="off"
          autoCapitalize="words"
          classes={{
            root: "text-field-root",
            notchedOutline: "text-notchedOutline",
            input: "text-field-input",
          }}
          endAdornment={
            <Tooltip
              id="clear-btn-tooltip"
              classes={{
                tooltip: "tooltip-clear-btn",
                popper: "popper-clear-btn",
                tooltipPlacementTop: "tooltip-top",
              }}
              title={val === "" ? "" : "Clear Field"}
              placement="top"
            >
              <InputAdornment
                className="text-field-clear-adornment"
                id="text-adornment"
                position="end"
                style={
                  val === "" ||
                  readOnly ||
                  handleInputDisable(idProp, state, disabledInput, userId) ===
                    true
                    ? { visibility: "hidden" }
                    : { visibility: "visible" }
                }
              >
                <IconButton
                  style={
                    val === ""
                      ? {
                          pointerEvents: "none",
                          cursor: "default",
                        }
                      : {
                          pointerEvents: "auto",
                          cursor: "pointer",
                        }
                  }
                  aria-label="clear input field"
                  onClick={() => {
                    if (debouncedSave) {
                      let newState = {
                        ...state,
                        [idProp]: "",
                      };
                      setState(newState);
                      setIsLoading(true);
                      setPlaceholder("");
                      setShowPlaceholder(false);
                      debouncedSave.mutate(newState);
                    } else {
                      let newState = {
                        ...state,
                        authorizationLetter: {
                          ...state["authorizationLetter"],
                          [idProp]: "",
                        },
                      };
                      setState(newState);
                      setPlaceholder("");
                      setShowPlaceholder(false);
                    }
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                  edge="end"
                  id="clear-icon-button"
                  disabled={val === "" ? true : false}
                >
                  <CloseIcon className="close-icon" />
                </IconButton>
              </InputAdornment>
            </Tooltip>
          }
        />
      </FormControl>
      <input
        key={`${idProp}-placeholder`}
        className={`${
          req ? "predictive-placeholder shift" : "predictive-placeholder"
        }`}
        type="text"
        autoComplete="off"
        id="predictive-placeholder"
        value={placeholder}
        disabled={true}
      />
      <Tooltip
        id="tf-tooltip"
        classes={{
          tooltip: "text-field-tooltip",
          popper: "text-field-popper",
          arrow: "text-field-arrow",
          tooltipPlacementLeft: "text-field-right",
        }}
        title={Messages[`${idProp}Message`]}
        placement={"top"}
        arrow
        open={open}
        sx={{
          backgroundColor: "rgb(0, 0, 0, 0.8) !important",
        }}
      >
        <InfoCircle
          className="text-info-circ"
          id={`info-${idProp}`}
          color="white"
          size={30}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
        />
      </Tooltip>
    </div>
  );
};
export default AuthorizedUserInput;
